

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  Autocomplete,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { API_BASE_URL } from '../../../config';
import api from '../../../api/axiosInterceptor';

// Convert DD-MM-YYYY or DD/MM/YYYY to ISO YYYY-MM-DD for input[type="date"]
const convertDisplayDateToISO = (dateStr) => {
  if (!dateStr) return '';
  // Support both '/' and '-' separators
  const parts = dateStr.includes('/') ? dateStr.split('/') : dateStr.split('-');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  // pad month/day if needed
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const AdminAsset32Component = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [feOptions, setFeOptions] = useState([]);
  const [feLoading, setFeLoading] = useState(true);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [formData, setFormData] = useState({
    notification: '',
    date: '',
    notification_no: '',
    fe: null,
  });

  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl. No', width: 70 },
    { id: 'fe_name', field: 'fe_name', headerName: 'Financial Establishment', width: 200 },
    { id: 'designation', field: 'designation', headerName: 'Designation', width: 150 },
    { id: 'phone', field: 'phone', headerName: 'Phone Number', width: 150 },
    { id: 'email', field: 'email', headerName: 'Email', width: 200 },
    { id: 'notification', field: 'notification', headerName: 'Notification', width: 300 },
    { id: 'date', field: 'date', headerName: 'Date', width: 150 },
    { id: 'notificationNo', field: 'notificationNo', headerName: 'Notification No', width: 180 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchAssignedFEs();
    fetchData();
  }, []);

  const fetchAssignedFEs = () => {
    setFeLoading(true);
    axios.get(`${API_BASE_URL}/home-notification/assignedfe/`)
      .then(res => setFeOptions(res.data))
      .catch(() => {
        setSnackbarMessage('Failed to load Financial Establishment list.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => setFeLoading(false));
  };

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/asset-mgmt/asset32/`)
      .then(response => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          fe_name: item.fe?.name_of_fe || '-',
          designation: item.fe?.designation || '-',
          phone: item.fe?.ph_number || '-',
          email: item.fe?.email || '-',
          notification: item.notification || '-',
          date: item.date ? new Date(item.date).toLocaleDateString('en-GB').replace(/\//g, '-') : '-',
          notificationNo: item.notification_no || '-',
          feId: item.fe?.id || null,
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load Asset 3(2) data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedRecord(null);
    setFormData({
      notification: '',
      date: '',
      notification_no: '',
      fe: null,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (record) => {
    const selectedFe = feOptions.find(fe => fe.id === record.feId) || null;
    setIsEditMode(true);
    setSelectedRecord(record);
    setFormData({
      notification: record.notification,
      date: convertDisplayDateToISO(record.date),
      notification_no: record.notificationNo,
      fe: selectedFe,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (record) => {
    setDeleteRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/asset-mgmt/asset32/${deleteRecord.id}/`)
      .then(() => {
        setSnackbarMessage('Record deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchData();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete record.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteRecord(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.notification?.trim()) errors.notification = 'Notification is required';
    if (!formData.notification_no?.trim()) errors.notification_no = 'Notification No is required';
    if (!formData.date) errors.date = 'Date is required';
    if (!formData.fe) errors.fe = 'Financial Establishment is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;

    setSubmitLoading(true);
    const payload = {
      notification: formData.notification,
      date: formData.date,
      notification_no: formData.notification_no,
      fe: formData.fe.id,
    };

    const request = isEditMode
      ? api.patch(`/asset-mgmt/asset32/${selectedRecord.id}/`, payload)
      : api.post(`/asset-mgmt/asset32/`, payload);

    request
      .then(() => {
        setSnackbarMessage(isEditMode ? 'Record updated successfully.' : 'Record added successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchData();
      })
      .catch(() => {
        setSnackbarMessage(isEditMode ? 'Failed to update record.' : 'Failed to add record.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setSubmitLoading(false);
        setOpenFormDialog(false);
      });
  };

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">Admin Asset 3(2)</Typography>
        <Button variant="contained" onClick={handleAdd}>Add Record</Button>
      </Box>

      {loading ? <LoadingSpinner size={30} /> : (
        <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
          },
        }} />
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? 'Edit Record' : 'Add Record'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Notification"
            fullWidth
            margin="dense"
            value={formData.notification}
            onChange={(e) => setFormData({ ...formData, notification: e.target.value })}
            error={!!formErrors.notification}
            helperText={formErrors.notification}
          />
          <TextField
            label="Date"
            type="date"
            fullWidth
            margin="dense"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            error={!!formErrors.date}
            helperText={formErrors.date}
          />
          <TextField
            label="Notification No"
            fullWidth
            margin="dense"
            value={formData.notification_no}
            onChange={(e) => setFormData({ ...formData, notification_no: e.target.value })}
            error={!!formErrors.notification_no}
            helperText={formErrors.notification_no}
          />
          <Autocomplete
            options={feOptions}
            getOptionLabel={(option) => option.name_of_fe || ''}
            loading={feLoading}
            value={formData.fe}
            onChange={(event, newValue) => setFormData({ ...formData, fe: newValue })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Financial Establishment"
                margin="dense"
                error={!!formErrors.fe}
                helperText={formErrors.fe}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {feLoading ? <LoadingSpinner size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit} disabled={submitLoading}>
            {submitLoading ? <LoadingSpinner size={20} /> : (isEditMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={submitLoading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};
